import React, { useEffect } from "react";
import { Switch, Route } from "wouter";
import { QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "@/components/shared/theme-provider";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/toaster";
import { useTranslation } from "react-i18next";
import { queryClient } from "@/lib/queryClient";
import { useAuth } from "@/hooks/use-auth";
import { isFeatureEnabled } from "@/config/features";
import { getEnabledRoutes, isRouteEnabled } from "@/config/routes";
import { NotificationProvider } from "@/contexts/NotificationContext";

// Import pages
import Login from "./pages/login.tsx";
import UnifiedDashboard from "./pages/unified-dashboard";
import Rooms from "./pages/rooms";
import Bookings from "./pages/bookings";
import UserManagement from "./pages/user-management";
import Reviews from "./pages/reviews";
import Coupons from "./pages/coupons";
import Support from "./pages/support";
import Notifications from "./pages/notifications";
import Settings from "./pages/settings";
import StaticContent from "./pages/static-content";
import ProfileSettings from "./pages/profile-settings";
import PaymentsLogs from "./pages/payments-logs";
import PaymentsTransactionDetails from "./pages/payments-transaction-details";
import PaymentsTests from "./pages/payments-tests";
import PaymentSuccess from "./pages/payment-success";
import Products from "./pages/products";
import Categories from "./pages/categories";
import Brands from "./pages/brands";
import Orders from "./pages/orders";
import MainLayout from "@/components/layout/main-layout";
import NotFound from "./pages/not-found";

// Dynamic route mapping
const routeComponents: Record<string, React.ComponentType<any>> = {
  '/dashboard': UnifiedDashboard, // Use unified dashboard as main dashboard
  '/rooms': Rooms,
  '/bookings': Bookings,
  '/user-management': UserManagement,
  '/reviews': Reviews,
  '/coupons': Coupons,
  '/support': Support,
  '/notifications': Notifications,
  '/settings': Settings,
  '/static-content': StaticContent,
  '/profile-settings': ProfileSettings,
  '/payments/logs': PaymentsLogs,
  '/payments/transactions/:id': PaymentsTransactionDetails,
  '/payments/tests': PaymentsTests,
  '/products': Products,
  '/categories': Categories,
  '/brands': Brands,
  '/orders': Orders,
};

function AuthenticatedRoutes() {
  const enabledRoutes = getEnabledRoutes();

  return (
    <Switch>
      {/* Payment success callback route - no layout needed */}
      <Route path="/payment/success" component={PaymentSuccess} />
      
      {/* All other routes with MainLayout */}
      <MainLayout>
        <Switch>
          {/* Root route redirects to dashboard */}
          <Route path="/" component={UnifiedDashboard} />
          
          {/* Dashboard route */}
          <Route path="/dashboard" component={UnifiedDashboard} />
          
          {/* Dynamic routes based on feature configuration */}
          {enabledRoutes.map((route) => {
            const Component = routeComponents[route.path];
            // Skip dashboard route since we handle it explicitly above
            if (Component && isRouteEnabled(route.path) && route.path !== '/dashboard') {
              return (
                <Route key={route.path} path={route.path} component={Component} />
              );
            }
            return null;
          })}
          
          {/* Fallback route for 404 */}
          <Route component={NotFound} />
        </Switch>
      </MainLayout>
    </Switch>
  );
}

// Global render counter to persist across component remounts
let globalRenderCount = 0;

function AppRoutes() {
  const { user, isLoading } = useAuth();

  // Debug logging with persistent render count to detect loops
  globalRenderCount += 1;
  
  // Only log every few renders to prevent console spam
  if (globalRenderCount <= 5 || globalRenderCount % 5 === 0) {
    console.log(`🔐 AppRoutes render #${globalRenderCount}:`, { 
      user: user ? { id: user.id, name: user.name } : null, 
      isLoading,
      hasUser: !!user,
      currentPath: window.location.pathname,
      authEnabled: isFeatureEnabled('authentication'),
      localStorage: {
        user: !!localStorage.getItem('user'),
        authToken: !!localStorage.getItem('authToken'),
        attemptedAuth: !!localStorage.getItem('attempted_real_auth')
      }
    });
  }

  // Detect potential infinite loops
  if (globalRenderCount > 10) {
    console.error(`🚨 POTENTIAL INFINITE LOOP DETECTED - AppRoutes rendered ${globalRenderCount} times!`);
    console.error('🚨 This usually indicates a routing or state management issue');
    
    // Force break the loop after too many renders
    if (globalRenderCount > 50) {
      console.error('🚨 FORCING LOOP BREAK - Too many renders detected!');
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Render Loop Detected</h1>
            <p className="text-gray-600 mb-4">The app has detected an infinite render loop.</p>
            <button 
              onClick={() => {
                globalRenderCount = 0;
                window.location.reload();
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Reset and Reload
            </button>
          </div>
        </div>
      );
    }
  }

  // Add timeout safeguard for loading state
  React.useEffect(() => {
    if (isLoading) {
      const timeout = setTimeout(() => {
        console.warn('🚨 Loading state timeout - forcing app to render');
        // This will force a re-render and potentially break the loading loop
      }, 5000); // 5 second timeout
      
      return () => clearTimeout(timeout);
    }
  }, [isLoading]);

  if (isLoading) {
    console.log(`🔄 App is loading... (render #${globalRenderCount})`);
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <div className="ml-4 text-muted-foreground">
          Loading... {globalRenderCount > 5 ? `(${globalRenderCount} renders)` : ''}
        </div>
      </div>
    );
  }

  return (
    <Switch>
      {/* Login route - only show if authentication is enabled */}
      {isFeatureEnabled('authentication') && (
        <Route path="/login" component={Login} />
      )}
      
      {/* Handle routing based on authentication state */}
      {user ? (
        // User is authenticated - show authenticated routes
        <Route path="*" component={AuthenticatedRoutes} />
      ) : (
        // No user - check if authentication is required
        isFeatureEnabled('authentication') ? (
          // Authentication required but no user - redirect to login
          <Route path="*" component={Login} />
        ) : (
          // Authentication disabled - show app directly
          <Route path="*" component={AuthenticatedRoutes} />
        )
      )}
    </Switch>
  );
}

function App() {
  const { i18n, ready } = useTranslation();

  useEffect(() => {
    if (ready && i18n.language) {
      // Set document direction based on language
      document.documentElement.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
      document.documentElement.lang = i18n.language;
    }
  }, [i18n.language, ready]);

  // Show loading while i18n is initializing
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TooltipProvider>
          <NotificationProvider>
            <Toaster />
            <AppRoutes />
          </NotificationProvider>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;